import { useEffect } from 'react';
import Checkbox from '@/Components/Checkbox';
import GuestLayout from '@/Layouts/GuestLayout';
import InputError from '@/Components/InputError';
import InputLabel from '@/Components/InputLabel';
import PrimaryButton from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import { Head, Link, useForm } from '@inertiajs/react';

export default function Login({ status, canResetPassword }) {
    const { data, setData, post, processing, errors, reset } = useForm({
        email: '',
        password: '',
        remember: false,
    });

    useEffect(() => {
        return () => {
            reset('password');
        };
    }, []);

    const submit = (e) => {
        e.preventDefault();

        post(route('login'));
    };

    return (
        <GuestLayout>
            <Head title="Sign In" />

            <div className="relative isolate overflow-hidden bg-slate-900 text-white rounded-2xl shadow-2xl p-8">
                <div className="absolute inset-0 opacity-60 blur-3xl bg-gradient-to-br from-sky-500/30 via-emerald-400/20 to-indigo-500/30"></div>
                <div className="relative flex flex-col gap-6">
                    <div>
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/10 backdrop-blur">
                            Internal Access
                        </div>
                        <h1 className="mt-4 text-3xl font-bold tracking-tight">Signal Monitor</h1>
                        <p className="mt-2 text-sm text-slate-200/80">
                            Sign in to keep tabs on content signals, alerts, and checks. Private access only.
                        </p>
                    </div>

                    {status && <div className="rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 px-4 py-2 text-sm">{status}</div>}

                    <form onSubmit={submit} data-test="login-form" className="space-y-4">
                        <div>
                            <InputLabel htmlFor="email" value="Email" className="text-slate-100" />
                            <TextInput
                                id="email"
                                type="email"
                                name="email"
                                value={data.email}
                                className="mt-1 block w-full bg-white/10 border-white/20 text-white placeholder:text-slate-300"
                                autoComplete="username"
                                isFocused={true}
                                onChange={(e) => setData('email', e.target.value)}
                                data-test="email-input"
                            />
                            <InputError message={errors.email} className="mt-2 text-red-200" data-test="email-error" />
                        </div>

                        <div>
                            <InputLabel htmlFor="password" value="Password" className="text-slate-100" />
                            <TextInput
                                id="password"
                                type="password"
                                name="password"
                                value={data.password}
                                className="mt-1 block w-full bg-white/10 border-white/20 text-white placeholder:text-slate-300"
                                autoComplete="current-password"
                                onChange={(e) => setData('password', e.target.value)}
                                data-test="password-input"
                            />
                            <InputError message={errors.password} className="mt-2 text-red-200" data-test="password-error" />
                        </div>

                        <div className="flex items-center justify-between text-sm text-slate-200/90">
                            <label className="flex items-center gap-2">
                                <Checkbox
                                    name="remember"
                                    checked={data.remember}
                                    onChange={(e) => setData('remember', e.target.checked)}
                                />
                                <span>Remember me</span>
                            </label>
                            {canResetPassword && (
                                <Link
                                    href={route('password.request')}
                                    className="underline hover:text-white"
                                >
                                    Forgot password?
                                </Link>
                            )}
                        </div>

                        <PrimaryButton className="w-full justify-center bg-emerald-500 hover:bg-emerald-600 border-0" disabled={processing} data-test="login-button">
                            {processing ? 'Signing in…' : 'Sign in'}
                        </PrimaryButton>
                    </form>
                </div>
            </div>
        </GuestLayout>
    );
}
